/**
 * Offline send queue — Family (CLAUDE.md §6 madde 11–12, §8 idempotency)
 *
 * Algorithm:
 *   1. UI calls `enqueueMessage(...)` → row appended to `outbox` with
 *      status='queued', plus an optimistic mirror in `messages` with
 *      localStatus='pending' so the chat bubble appears immediately.
 *   2. `processQueue()` is invoked on:
 *        - app startup (online),
 *        - online event from `useOnlineStatus`,
 *        - tab visibility regain,
 *        - manual retry (`retryQueueItem`).
 *      It iterates `status='queued'` rows in insertion order, sets each to
 *      'sending', attempts `messages.insert(...)`, and on success deletes
 *      the row + bumps the local mirror to 'sent'.
 *   3. On failure, retries++ and status='failed' are persisted. After
 *      `MAX_RETRIES`, the row is left in 'failed' state and the UI must
 *      surface a manual retry. Only `retryQueueItem(id)` re-arms it.
 *
 * Idempotency contract:
 *   The server enforces UNIQUE (sender_id, client_msg_id) on `messages`.
 *   We generate the UUID at enqueue time. If we ever crash between
 *   "row inserted on server" and "row deleted from outbox", the next pass
 *   will hit a 23505. `messages/send.ts::insertMessage` already handles this
 *   by re-fetching the existing row, so we simply call into it and never
 *   double-send.
 *
 * Retry budget rationale (3 attempts):
 *   - Transient network errors (3G drop, captive portal) usually resolve in
 *     seconds. 3 attempts spread across an exponential-ish app cadence
 *     (online event, visibility, manual) is plenty.
 *   - Permanent failures (RLS reject, schema drift) shouldn't loop forever
 *     — capping at 3 keeps the outbox finite and avoids battery drain.
 *
 * What this layer does NOT do:
 *   - It does not call the translate Edge Function. The send-side fanout is
 *     owned by `messages/send.ts::fanoutTranslations` and triggered by the
 *     UI after the message-row insert succeeds (or by a separate edge-cron
 *     reconciler in Phase 1.5 if needed).
 */

import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import type { Lang } from '@/types/lang';
import { getDb } from './dexie';
import type { MessageLocal, OutboxItem } from './types';

const MAX_RETRIES = 3;

/**
 * UUID generator. Uses crypto.randomUUID where available (all evergreen
 * browsers + Node 19+), falls back to a manual v4 for jsdom-without-crypto
 * test environments.
 */
function newUuid(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  // Fallback v4 — RFC4122-shaped, not cryptographically-strong. Fine for
  // idempotency tokens; not used for security purposes here.
  const bytes = new Uint8Array(16);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  // Set version (4) and variant (10xx) bits per RFC4122.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface EnqueueInput {
  conversationId: string;
  body: string;
  sourceLang: Lang;
  /** Caller may supply their own UUID (e.g. for optimistic UI keying). */
  clientMsgId?: string;
  /** Sender id is needed for the local optimistic mirror only. */
  senderId: string;
}

export interface EnqueueResult {
  outboxId: number;
  clientMsgId: string;
  /** The optimistic message row inserted into the local `messages` mirror. */
  message: MessageLocal;
}

/**
 * Append a message to the outbox + create its optimistic local mirror.
 *
 * Idempotent: if `clientMsgId` already exists in `outbox`, the existing row
 * is returned untouched. This protects against double-tap of the send button
 * or a UI re-render that re-runs the enqueue path with the same id.
 */
export async function enqueueMessage(input: EnqueueInput): Promise<EnqueueResult> {
  const db = getDb();
  const clientMsgId = input.clientMsgId ?? newUuid();
  const now = new Date().toISOString();

  // Optimistic mirror id — also a UUID, lives in `messages.id`. The server
  // INSERT will return its own canonical id; we rewrite the local row to use
  // that one in `processQueue` to avoid duplicate bubbles.
  const optimisticId = newUuid();

  const optimistic: MessageLocal = {
    id: optimisticId,
    conversation_id: input.conversationId,
    sender_id: input.senderId,
    source_lang: input.sourceLang,
    body: input.body,
    client_msg_id: clientMsgId,
    delivered_at: null,
    created_at: now,
    deleted_at: null,
    localStatus: 'pending',
  };

  const outbox: OutboxItem = {
    client_msg_id: clientMsgId,
    conversation_id: input.conversationId,
    body: input.body,
    source_lang: input.sourceLang,
    created_at: now,
    retries: 0,
    status: 'queued',
  };

  // Use a transaction so the mirror and outbox land atomically — if one fails
  // (quota exceeded, etc.) the other rolls back and the UI doesn't end up with
  // a "ghost" optimistic bubble that has no outbox entry.
  const outboxId = await db.transaction('rw', db.messages, db.outbox, async () => {
    // If the same client_msg_id was already enqueued, short-circuit and return
    // the existing outbox id. Don't double-insert into `messages` either.
    const dup = await db.outbox.where('client_msg_id').equals(clientMsgId).first();
    if (dup) {
      return dup.id ?? -1;
    }
    await db.messages.put(optimistic);
    return await db.outbox.add(outbox);
  });

  return {
    outboxId,
    clientMsgId,
    message: optimistic,
  };
}

/**
 * Internal: send a single outbox row to the server. Returns the canonical
 * server message id on success.
 *
 * Throws on infrastructure error so the caller (`processQueue`) can bump
 * retries.
 */
async function sendOne(item: OutboxItem): Promise<{ serverId: string }> {
  if (!isSupabaseConfigured) {
    throw new Error('Supabase not configured');
  }
  const supabase = getSupabase();
  const userResp = await supabase.auth.getUser();
  const userId = userResp.data.user?.id;
  if (!userId) {
    // No session → can't send. Don't increment retries for auth-loss; the user
    // will reconnect and try again. We surface the error to the caller.
    throw new Error('No authenticated user');
  }

  const insert = await supabase
    .from('messages')
    .insert({
      conversation_id: item.conversation_id,
      sender_id: userId,
      source_lang: item.source_lang,
      body: item.body,
      client_msg_id: item.client_msg_id,
    })
    .select('id')
    .single();

  if (insert.error) {
    // 23505 = UNIQUE violation → server already has this client_msg_id from a
    // prior attempt. Re-fetch the row to get its canonical id and treat as
    // success.
    if (insert.error.code === '23505') {
      const existing = await supabase
        .from('messages')
        .select('id')
        .eq('sender_id', userId)
        .eq('client_msg_id', item.client_msg_id)
        .single();
      if (existing.error || !existing.data) {
        throw new Error(`23505 but no row: ${existing.error?.code ?? 'unknown'}`);
      }
      return { serverId: existing.data.id as string };
    }
    throw new Error(`${insert.error.code ?? 'unknown'} ${insert.error.message}`);
  }
  if (!insert.data) {
    throw new Error('insert returned no row');
  }
  return { serverId: insert.data.id as string };
}

/**
 * Drain the outbox.
 *
 * - If offline, this is a no-op. The `online` event listener in `auto-sync.ts`
 *   re-invokes this on reconnect.
 * - Iterates `status='queued'` rows (not 'failed' — those are user-gated).
 * - Each row is set to 'sending' before the network call to prevent a parallel
 *   invocation (e.g. visibility + online firing back-to-back) from
 *   double-attempting the same row.
 *
 * The function is intentionally not concurrent-safe across multiple tabs.
 * Two open tabs of the app may race and both insert with the same
 * `client_msg_id`; the server's UNIQUE constraint and our 23505 handler
 * resolve that to a single row + a re-fetch.
 */
export async function processQueue(): Promise<{
  attempted: number;
  succeeded: number;
  failed: number;
}> {
  const db = getDb();
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { attempted: 0, succeeded: 0, failed: 0 };
  }

  // Fetch + sort outside a transaction — Dexie transactions can't make network
  // calls. We claim each row by status='sending' before sending so concurrent
  // calls don't both attempt the same row.
  const queued = await db.outbox.where('status').equals('queued').sortBy('id');
  let attempted = 0;
  let succeeded = 0;
  let failed = 0;

  for (const item of queued) {
    if (item.id === undefined) continue;
    if (item.retries >= MAX_RETRIES) continue;

    // Atomically claim — if status changed underneath us, skip.
    const claimed = await db.transaction('rw', db.outbox, async () => {
      const fresh = await db.outbox.get(item.id!);
      if (!fresh || fresh.status !== 'queued') return false;
      await db.outbox.update(item.id!, { status: 'sending' });
      return true;
    });
    if (!claimed) continue;

    attempted++;
    try {
      const { serverId } = await sendOne(item);

      // Replace the optimistic local row's id with the server's canonical id
      // and bump status to 'sent'. The mirror row was inserted with a local
      // UUID; we must not leave both copies around.
      await db.transaction('rw', db.messages, db.outbox, async () => {
        const optimistic = await db.messages
          .where('client_msg_id')
          .equals(item.client_msg_id)
          .first();
        if (optimistic) {
          if (optimistic.id !== serverId) {
            await db.messages.delete(optimistic.id);
          }
          await db.messages.put({
            ...optimistic,
            id: serverId,
            localStatus: 'sent',
          });
        }
        await db.outbox.delete(item.id!);
      });
      succeeded++;
    } catch (err) {
      const errMsg = String(err).slice(0, 200);
      const nextRetries = (item.retries ?? 0) + 1;
      await db.outbox.update(item.id, {
        status: 'failed',
        retries: nextRetries,
        lastError: errMsg,
      });
      // Mirror status: failed if max retries hit, otherwise leave as 'pending'
      // so the UI doesn't show a permanent failure for transient drops.
      const mirrorStatus: MessageLocal['localStatus'] =
        nextRetries >= MAX_RETRIES ? 'failed' : 'pending';
      const optimistic = await db.messages
        .where('client_msg_id')
        .equals(item.client_msg_id)
        .first();
      if (optimistic) {
        await db.messages.update(optimistic.id, { localStatus: mirrorStatus });
      }
      failed++;
    }
  }

  return { attempted, succeeded, failed };
}

/**
 * Re-arm a failed/sending outbox item for another pass.
 *
 * Resets status='queued'. Does NOT reset `retries` — by design: the user
 * gets MAX_RETRIES total attempts per `client_msg_id` regardless of how many
 * times they manually retry. Set `resetRetries=true` to override (admin/debug).
 */
export async function retryQueueItem(
  id: number,
  opts?: { resetRetries?: boolean },
): Promise<void> {
  const db = getDb();
  const patch: Partial<OutboxItem> = { status: 'queued' };
  if (opts?.resetRetries) patch.retries = 0;
  await db.outbox.update(id, patch);
  // Best-effort: kick the queue. If we're offline, this is a no-op.
  await processQueue();
}

/** Remove an outbox row (e.g. user explicitly cancels a stuck send). */
export async function removeQueueItem(id: number): Promise<void> {
  const db = getDb();
  const item = await db.outbox.get(id);
  if (!item) return;
  await db.transaction('rw', db.messages, db.outbox, async () => {
    await db.outbox.delete(id);
    const optimistic = await db.messages
      .where('client_msg_id')
      .equals(item.client_msg_id)
      .first();
    if (optimistic) {
      await db.messages.delete(optimistic.id);
    }
  });
}

/** Read-only: count of items pending the user's attention (queued or failed). */
export async function pendingQueueCount(): Promise<number> {
  const db = getDb();
  return await db.outbox
    .where('status')
    .anyOf('queued', 'failed', 'sending')
    .count();
}
